import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const deviceScreenDiveSkill: CodeoSkill = {
    name: SKILL_NAMES.deviceScreenDive,
    kind: 'codeo',
    description:
        'Hold a phone or laptop in the frame with its screen live, then move into the screen until ' +
        'it becomes the frame — for showing a product in its context and then in its detail.',
    documentUsesSkill: check(
        (script) =>
            /rotationY|perspective/.test(script) ||
            valuesOf(script, 'scale').some((value) => value >= 2),
    ),
    instructions: `# Device Screen Dive

A device sits in the frame at an angle with something happening on its screen. The camera moves toward it, the device's chrome and perspective flatten away, and the screen becomes the whole frame. It answers "where does this live?" and "what does it look like?" in one continuous move instead of two scenes.

The flattening is what makes it work: the screen must un-rotate as it grows, so the arrival is a straight-on view rather than a magnified perspective shot.

## Markup

Build the screen at final full-frame proportions and let the device wrapper apply the perspective. Everything sits inside one stage that scales.

\`\`\`html
<div id="dive-stage" class="absolute inset-0 overflow-hidden flex flex-col justify-center items-start gap-10 p-24" style="perspective: 1600px">
  <div id="device" style="width: 700px; height: 440px; border-radius: 26px; padding: 14px; background: var(--l5-color-1, #101820); box-shadow: 0 50px 110px rgba(0,0,0,0.45)">
    <div id="screen" class="w-full h-full overflow-hidden" style="position: relative; border-radius: 14px; background: var(--l5-color-0, #f4f6f9);">
      <div style="position: absolute; left: 8%; top: 34%; font-family: var(--l5-font-0), sans-serif; font-size: 58px; font-weight: 800; color: var(--l5-color-2, #38bdf8);">Ready to publish</div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

Rotation, scale and position resolve together on one tween, so the move reads as a single camera decision.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const DIVE = 1.3;

gsap.set('#device', { rotationY: -26, rotationX: 6, scale: 1, transformOrigin: '50% 50%' });

tl.from('#device', { opacity: 0, y: 40, duration: 0.7, ease: 'power3.out' }, 0.2);
tl.to('#device', {
    rotationY: 0,
    rotationX: 0,
    scale: 2.85,
    duration: 1.5,
    ease: 'power2.inOut',
}, DIVE);
// The chrome dissolves as the screen takes the frame, so the device does not simply get bigger.
tl.to('#device', { borderRadius: 0, padding: 0, duration: 0.6, ease: 'power2.inOut' }, DIVE + 0.7);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Dive depth**: scale until the screen's rendered width exceeds the frame — for a 700px device at 1920 wide that is about 2.8×. Under-scaling leaves a visible border of the old background.
- **Approach angle**: \`rotationY: -26\` is a confident three-quarter view. Past about 35° the screen content is too foreshortened to read before the dive starts.
- **Pull out instead**: reversing the tween starts inside the product and pulls back to reveal the device it runs on, which suits an opening.
- **Multiple devices**: splay two or three, then dive into one. The others sliding out of frame as the chosen one grows is the handoff — see \`${SKILL_NAMES.elementMorphHandoff}\`.
- **Screen content**: let it animate *before* the dive, hold still during it, and resume after. Motion during the move competes with the camera.
- **Laptop**: same technique with a wider aspect and a base; the base must scale out of frame rather than fading, or it reads as evaporating.

## Pitfalls

- The device is transform-tweened on several channels, so it must not carry a CSS \`transform\`. \`perspective\` belongs on the *stage*, not on the device.
- Screen content is authored at device size and magnified nearly 3×, so a 58px label ends up effectively 165px. Compose the arrival frame, not just the establishing one.
- The device grows well past the frame edges, so it needs \`data-l5-allow-offscreen\`.
- Don't keep the device chrome all the way in. A rounded bezel still visible at full frame reads as a mistake; dissolve it as the screen takes over.
- Rasterizing a large 3D-transformed subtree is expensive. Keep the screen's contents to a few elements.
- This move needs about 1.5s. Rushed, it reads as a zoom glitch rather than a camera.
- Don't use it when the product is not actually the point. A device dive is a claim about software; on anything else it is furniture.`,
};
